/**
 * apiCache — prosty cache w pamięci dla odpowiedzi GET z axios.
 *
 * Zasady:
 * - Cachujemy tylko GET-y. Mutacje (POST/PUT/PATCH/DELETE) automatycznie unieważniają
 *   cache dla danego zasobu (np. POST /api/invoices → unieważnia wszystkie GET-y /api/invoices*).
 * - TTL domyślnie 45 s — w sam raz dla dashboardów (świeże dane bez nadmiernego fetchowania).
 * - `?nocache=1` w URL omija cache (przycisk „Odśwież").
 *
 * Wpięte globalnie w axios interceptorze (App.js).
 */

const DEFAULT_TTL_MS = 45 * 1000;
const cache = new Map(); // key (METHOD URL) → { data, expiresAt, status, headers }
const inflight = new Map(); // key → Promise (deduplikacja równoległych requestów)

const makeKey = (method, url, params) => {
  const base = `${(method || 'GET').toUpperCase()} ${url}`;
  if (!params) return base;
  const sp = new URLSearchParams();
  Object.keys(params).sort().forEach((k) => {
    if (params[k] !== undefined && params[k] !== null) sp.set(k, String(params[k]));
  });
  const q = sp.toString();
  return q ? `${base}?${q}` : base;
};

const resourcePrefix = (url) => {
  // /api/invoices/abc/selected → /api/invoices
  try {
    const u = url.startsWith('http') ? new URL(url).pathname : url;
    const parts = u.split('/').filter(Boolean); // ['api', 'invoices', 'abc', 'selected']
    if (parts[0] === 'api' && parts[1]) return `/api/${parts[1]}`;
    return u;
  } catch {
    return url;
  }
};

export const apiCache = {
  get(key) {
    const entry = cache.get(key);
    if (!entry) return null;
    if (Date.now() > entry.expiresAt) {
      cache.delete(key);
      return null;
    }
    return entry;
  },
  set(key, value, ttlMs = DEFAULT_TTL_MS) {
    cache.set(key, { ...value, expiresAt: Date.now() + ttlMs });
  },
  invalidate(prefix) {
    for (const key of cache.keys()) {
      if (key.includes(prefix)) cache.delete(key);
    }
  },
  invalidateAll() {
    cache.clear();
  },
  pending(key) { return inflight.get(key); },
  setPending(key, p) { inflight.set(key, p); },
  clearPending(key) { inflight.delete(key); },
  size() { return cache.size; },
};

export const cacheKeyForRequest = (config) => makeKey(config.method, config.url, config.params);
export const isCacheableRequest = (config) => {
  const m = (config.method || 'GET').toUpperCase();
  if (m !== 'GET') return false;
  if (config.skipCache) return false;
  const url = config.url || '';
  if (url.includes('nocache=1')) return false;
  // Pomijamy też niektóre żądania, które MUSZĄ być żywe (komunikator typing, audit)
  if (url.includes('/communicator/typing')) return false;
  if (url.includes('/audit/')) return false;
  if (url.includes('/auth/')) return false;
  return true;
};
export const invalidateForMutation = (url) => {
  const prefix = resourcePrefix(url);
  apiCache.invalidate(prefix);
};

export default apiCache;
